import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import { collectDeskMedia, isMediaImage, listDeskReports, mediaHref } from "../../js/api/deskMedia";
import { peekBuyers } from "../../js/api/buyersApi";
import { useAgentScope } from "../../js/hooks/useAgentScope";
import { useDesk } from "../../js/hooks/useDesk";
import { formatDate, prettyValue } from "../../js/utils/formatters";

function peekSellers() {
  try {
    return JSON.parse(localStorage.getItem("dh_sellers") || "[]");
  } catch {
    return [];
  }
}

function stamp(iso) {
  if (!iso) return "";
  return formatDate(iso);
}

export default function AgentDocuments() {
  const desk = useDesk();
  const { hash } = useLocation();
  const { session, listAgentId, excludeAgentId } = useAgentScope();
  const [filter, setFilter] = useState(() => (hash === "#desk-reports" ? "reports" : "all"));
  const buyers = useMemo(() => {
    const rows = peekBuyers();
    if (listAgentId) return rows.filter((item) => item.agentId === listAgentId);
    if (excludeAgentId) return rows.filter((item) => item.agentId && item.agentId !== excludeAgentId);
    return rows;
  }, [listAgentId, excludeAgentId]);
  const sellers = peekSellers();
  const items = useMemo(
    () => collectDeskMedia({ session, buyers, sellers }),
    [session, buyers, sellers]
  );
  const reports = useMemo(() => listDeskReports(session), [session]);
  useEffect(() => {
    if (hash !== "#desk-reports") return undefined;
    const frame = document.querySelector(".desk-frame__body");
    if (frame) frame.scrollTop = 0;
    return undefined;
  }, [hash]);

  const visible = items.filter((item) => {
    if (filter === "photos") return isMediaImage(item);
    if (filter === "documents") return !isMediaImage(item) && item.kind !== "report";
    if (filter === "reports") return item.kind === "report";
    return true;
  });

  return (
    <AppLayout variant={desk.variant === "broker" ? "broker" : "agent"}>
      <div className="page-header" id="desk-reports">
        <div>
          <h1>Documents & Photos</h1>
          <p className="page-lead">
            One account vault for checklist uploads, message attachments, File Packet files, client
            photos, and saved reports. Items follow this signed-in desk — not a single device folder.
          </p>
        </div>
      </div>

      <div className="row" style={{ gap: "0.55rem", flexWrap: "wrap", marginBottom: "1rem" }}>
        {[
          { id: "all", label: "All" },
          { id: "photos", label: "Photos" },
          { id: "documents", label: "Documents" },
          { id: "reports", label: "Reports" },
        ].map((item) => (
          <button
            key={item.id}
            type="button"
            className={`ghost-btn${filter === item.id ? " is-selected" : ""}`}
            onClick={() => setFilter(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <section className="card stack" style={{ marginBottom: "1rem" }}>
        <h2>Reports</h2>
        <p className="muted">
          Communication reports and File Packet exports saved to this account. A report created on
          a phone appears here on the desktop when you sign in to the same account.
        </p>
        {reports.length ? (
          <ul className="media-grid media-grid--list">
            {reports.map((item) => (
              <li key={item.id}>
                <strong>{item.name}</strong>
                <div className="muted">
                  {stamp(item.stamp)}
                  {item.ownerName ? ` · ${item.ownerName}` : ""}
                  {item.exportType ? ` · ${prettyValue(item.exportType)}` : ""}
                </div>
                {mediaHref(item) ? (
                  <a href={mediaHref(item)} download={`${item.id}.html`}>
                    Download
                  </a>
                ) : (
                  <span className="muted">Stored with this account</span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No reports saved on this desk yet.</p>
        )}
      </section>

      <section className="card stack">
        <h2>{filter === "photos" ? "Photos" : filter === "documents" ? "Documents" : "Library"}</h2>
        {visible.length ? (
          <ul className="media-grid">
            {visible.map((item) => {
              const href = mediaHref(item);
              return (
                <li key={`${item.source}-${item.id}`} className="media-tile">
                  {isMediaImage(item) && href ? (
                    <img src={href} alt="" className="media-tile__img" />
                  ) : (
                    <div className="media-tile__placeholder">{prettyValue(item.kind || "file")}</div>
                  )}
                  <strong>{item.name}</strong>
                  <div className="muted">
                    {prettyValue(item.source)}
                    {item.ownerName ? ` · ${item.ownerName}` : ""}
                    {item.stamp ? ` · ${stamp(item.stamp)}` : ""}
                  </div>
                  {item.fileId ? (
                    <Link to={desk.file(item.fileId)}>Open File</Link>
                  ) : href ? (
                    <a href={href} download={item.name}>
                      Open
                    </a>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="muted">Nothing in this view yet. Checklist photos, message files, and packet attachments collect here.</p>
        )}
      </section>
    </AppLayout>
  );
}
